<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import SettingsHeader from 'dashboard/components-next/aifocus/pageComponents/settings/SettingsHeader.vue';

const props = defineProps({
  agentId: {
    type: Number,
    required: true,
  },
});

const store = useStore();

const agent = computed(() =>
  store.getters['aifocusAgents/getRecord'](props.agentId)
);

const isSaving = ref(false);

// Follow-up state
const followupEnabled = ref(false);
const followupPrompt = ref('');

// Each follow-up step with its own delay
const followupSteps = ref([
  { delayMinutes: 1, message: '' },
  { delayMinutes: 10, message: '' },
  { delayMinutes: 60, message: '' },
]);

// Predefined delay options
const delayOptions = [
  { value: 1, label: '1 minuto' },
  { value: 2, label: '2 minutos' },
  { value: 5, label: '5 minutos' },
  { value: 10, label: '10 minutos' },
  { value: 15, label: '15 minutos' },
  { value: 30, label: '30 minutos' },
  { value: 60, label: '1 hora' },
  { value: 120, label: '2 horas' },
  { value: 360, label: '6 horas' },
  { value: 720, label: '12 horas' },
  { value: 1440, label: '24 horas' },
];

const defaultPrompts = [
  'Ola! Vi que estávamos conversando. Posso te ajudar com algo mais?',
  'Oi! Ainda estou por aqui caso precise de alguma informação.',
  'Ola! Só passando para saber se posso te ajudar com alguma dúvida.',
];

const addStep = () => {
  followupSteps.value.push({ delayMinutes: 60, message: '' });
};

const removeStep = index => {
  if (followupSteps.value.length > 1) {
    followupSteps.value.splice(index, 1);
  }
};

const formatDelay = minutes => {
  const opt = delayOptions.find(o => o.value === minutes);
  return opt ? opt.label : `${minutes} min`;
};

const loadFromAgent = agentData => {
  if (!agentData) return;
  const config = agentData.config || {};
  followupEnabled.value = config.followup_enabled ?? false;
  followupPrompt.value = config.followup_prompt || '';

  // Load steps - support both old and new format
  if (config.followup_steps && config.followup_steps.length > 0) {
    followupSteps.value = config.followup_steps.map(s => ({
      delayMinutes: s.delay_minutes || 60,
      message: s.message || '',
    }));
  } else if (config.followup_max_count) {
    // Migrate from old format
    const count = config.followup_max_count || 3;
    const delay = config.followup_delay_minutes || 60;
    followupSteps.value = Array.from({ length: count }, () => ({
      delayMinutes: delay,
      message: '',
    }));
  }
};

watch(
  agent,
  newAgent => {
    if (newAgent) loadFromAgent(newAgent);
  },
  { immediate: true, deep: false }
);

const handleSave = async () => {
  isSaving.value = true;
  try {
    const steps = followupSteps.value.map(s => ({
      delay_minutes: Number(s.delayMinutes),
      message: s.message || '',
    }));

    await store.dispatch('aifocusAgents/update', {
      id: props.agentId,
      config: {
        ...(agent.value?.config || {}),
        followup_enabled: followupEnabled.value,
        followup_steps: steps,
        followup_max_count: steps.length,
        followup_delay_minutes: steps[0]?.delay_minutes || 60,
        followup_prompt: followupPrompt.value || null,
      },
    });
    await store.dispatch('aifocusAgents/sync', { id: props.agentId });
    await store.dispatch('aifocusAgents/show', props.agentId);
    useAlert('Follow-up salvo e sincronizado');
  } catch (error) {
    useAlert(error?.message || 'Erro ao salvar follow-up');
  } finally {
    isSaving.value = false;
  }
};
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="pb-6">
    <SettingsHeader
      heading="Follow-up Automatico"
      description="Configure mensagens automaticas de acompanhamento quando o cliente para de responder. Cada etapa pode ter seu próprio tempo de espera."
    />

    <!-- Follow-up Form -->
    <div class="mt-6 space-y-6">
      <!-- Enable Toggle -->
      <div
        class="flex items-center justify-between p-4 bg-n-solid-1 border border-n-weak rounded-xl"
      >
        <div class="flex items-center gap-3">
          <div
            class="w-10 h-10 rounded-lg flex items-center justify-center"
            :class="followupEnabled ? 'bg-green-500/20' : 'bg-n-alpha-2'"
          >
            <span
              class="text-xl"
              :class="
                followupEnabled
                  ? 'i-lucide-repeat text-green-500'
                  : 'i-lucide-repeat text-n-slate-10'
              "
            />
          </div>
          <div>
            <p class="font-medium text-n-slate-12">
              Ativar Follow-up Automatico
            </p>
            <p class="text-xs text-n-slate-10">
              Envia mensagens quando o cliente para de responder
            </p>
          </div>
        </div>
        <Switch v-model="followupEnabled" />
      </div>

      <!-- Settings (only shown when enabled) -->
      <div
        v-if="followupEnabled"
        class="space-y-5 p-5 bg-n-alpha-1 border border-n-weak rounded-xl"
      >
        <!-- Follow-up Steps -->
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            Etapas de Follow-up
          </label>
          <p class="text-xs text-n-slate-10 mb-2">
            Configure o tempo de espera e a mensagem de cada etapa. O tempo é
            contado a partir da ultima mensagem sem resposta do cliente.
          </p>

          <div class="space-y-3">
            <div
              v-for="(step, index) in followupSteps"
              :key="index"
              class="p-4 bg-n-solid-1 border border-n-weak rounded-lg"
            >
              <div class="flex items-center justify-between mb-3">
                <div class="flex items-center gap-2">
                  <span
                    class="flex-shrink-0 w-6 h-6 rounded-full bg-n-brand/20 text-xs font-bold flex items-center justify-center text-n-brand"
                  >
                    {{ index + 1 }}
                  </span>
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ index + 1 }}° Follow-up
                  </span>
                </div>
                <button
                  v-if="followupSteps.length > 1"
                  class="p-1 rounded hover:bg-red-500/10 text-n-slate-10 hover:text-red-500 transition-colors"
                  title="Remover etapa"
                  @click="removeStep(index)"
                >
                  <span class="i-lucide-trash-2 text-sm" />
                </button>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                <!-- Delay selector -->
                <div class="flex flex-col gap-1">
                  <label class="text-xs font-medium text-n-slate-11">
                    Tempo de espera
                  </label>
                  <ComboBox
                    v-model="step.delayMinutes"
                    :options="delayOptions"
                    placeholder="Selecione..."
                    search-placeholder="Buscar..."
                  />
                </div>

                <!-- Message -->
                <div class="flex flex-col gap-1">
                  <Input
                    v-model="step.message"
                    label="Mensagem (opcional)"
                    :placeholder="
                      defaultPrompts[index] ||
                      'Deixe vazio para mensagem gerada pela IA'
                    "
                    size="sm"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Add step button -->
          <button
            class="flex items-center gap-2 px-4 py-2 mt-1 text-sm font-medium text-n-brand border border-dashed border-n-brand/30 rounded-lg hover:bg-n-brand/5 transition-colors w-fit"
            @click="addStep"
          >
            <span class="i-lucide-plus text-sm" />
            Adicionar etapa
          </button>
        </div>

        <!-- Global Custom Prompt -->
        <TextArea
          v-model="followupPrompt"
          label="Prompt global de follow-up (opcional)"
          placeholder="Ex: Envie uma mensagem amigável perguntando se o cliente precisa de ajuda, variando o tom a cada follow-up."
          auto-height
          message="Instrucao para a IA gerar as mensagens de follow-up. Usado quando a mensagem da etapa esta vazia."
        />

        <!-- Timeline preview -->
        <div class="p-4 bg-n-solid-1 border border-n-weak rounded-lg">
          <p class="text-xs font-medium text-n-slate-11 mb-3">
            Linha do tempo:
          </p>
          <div class="flex items-center gap-1 flex-wrap">
            <span
              class="text-xs px-2 py-1 bg-n-alpha-2 rounded text-n-slate-11"
            >
              Cliente para de responder
            </span>
            <template
              v-for="(step, index) in followupSteps"
              :key="'timeline-' + index"
            >
              <span class="i-lucide-arrow-right text-n-slate-8 text-xs" />
              <span
                class="text-xs px-2 py-1 bg-n-brand/10 rounded text-n-brand font-medium"
              >
                {{ formatDelay(step.delayMinutes) }}
              </span>
              <span class="i-lucide-arrow-right text-n-slate-8 text-xs" />
              <span
                class="text-xs px-2 py-1 bg-green-500/10 rounded text-green-600 font-medium"
              >
                {{ index + 1 }}° msg
              </span>
            </template>
          </div>
        </div>
      </div>

      <!-- Save Button -->
      <div class="flex justify-end">
        <Button
          label="Salvar Follow-up"
          :is-loading="isSaving"
          @click="handleSave"
        />
      </div>
    </div>

    <!-- Info Card -->
    <div class="mt-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
      <div class="flex gap-3">
        <span class="i-lucide-info text-blue-400 text-xl flex-shrink-0" />
        <div>
          <h4 class="font-medium text-n-slate-12 mb-1">
            Como funciona o Follow-up
          </h4>
          <ul class="text-sm text-n-slate-11 space-y-1">
            <li>1. O cliente para de responder</li>
            <li>
              2. Após o tempo da 1° etapa, o agente envia o primeiro follow-up
            </li>
            <li>
              3. Se continuar sem resposta, aguarda o tempo da próxima etapa e
              envia novamente
            </li>
            <li>4. Repete até completar todas as etapas configuradas</li>
            <li>
              5. Se o cliente responder a qualquer momento, o follow-up para
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>
